import { Component, Input, OnDestroy, OnInit, SkipSelf } from '@angular/core';
import { ControlContainer, FormGroup } from '@angular/forms';
import { PagedResponse, SessionService } from '@edl-development/edl-common';
import { firstValueFrom } from 'rxjs';
import { PagedModel } from 'src/app/model/base/paged.model';
import { CustomFormSelectorDTO } from 'src/app/model/observation/custom-forms/custom-form-selector-dto.model';
import { CustomFormsService } from 'src/app/services/Observation/custom-forms/custom-forms.service';
import { RequestQueue } from 'src/app/utilities/request-queue/request-queue.util';

@Component({
    selector: 'paetep-custom-form-selector-paging',
    templateUrl: './custom-form-selector-paging.component.html',
    styleUrls: ['./custom-form-selector-paging.component.scss'],
    viewProviders: [{
        provide: ControlContainer,
        useFactory: (container: ControlContainer) => container,
        deps: [[new SkipSelf(), ControlContainer]]
    }]
})
export class CustomFormSelectorPagingComponent implements OnInit, OnDestroy {
    @Input() targetUserId?: string = '';
    @Input() limit: number = 5;

    public reloadQueue: RequestQueue<PagedResponse<CustomFormSelectorDTO> | undefined> = new RequestQueue<PagedResponse<CustomFormSelectorDTO> | undefined>();

    isBusy = false;
    formGroup!: FormGroup;
    pagedResponse?: PagedResponse<CustomFormSelectorDTO>;

    constructor(
        private sessionService: SessionService,
        private customFormService: CustomFormsService,
        private container: ControlContainer
    ) {}

    ngOnInit(): void {
        this.formGroup = this.container.control as FormGroup;
        this.fetchCustomForms();
    }

    ngOnDestroy(): void {
        this.reloadQueue.complete();
    }

    getPagingFormGroup = () => {
        return this.formGroup.get('Paging');
    }

    getPagingControl = (controlName: string) => {
        return this.getPagingFormGroup()?.get(controlName);
    }

    onCustomFormSelected = (customFormSelectorDTO: CustomFormSelectorDTO | null) => {
        const customFormName = this.formGroup.get('CustomFormTitle');
        customFormName?.setValue(customFormSelectorDTO?.title ?? '');
        customFormName?.updateValueAndValidity();
    }

    onCustomFormRemoved = () => {
        const customFormId = this.formGroup.get('CustomFormId');
        customFormId?.setValue('');
        customFormId?.updateValueAndValidity();

        this.onCustomFormSelected(null);
    }

    onSearchBy = () => {
        const searchControl = this.getPagingControl('SearchBy');

        if (!searchControl?.dirty) {
            return;
        }
        searchControl.markAsPristine();

        this.resetCurrentPage();
        this.fetchCustomForms();
    }

    onReset = () => {
        const form = this.getPagingFormGroup();
        form?.get('Ascending')?.setValue(true);
        form?.get('OrderBy')?.setValue('title');
        form?.get('SearchBy')?.setValue('');
        form?.get('SearchBy')?.markAsPristine();
        form?.get('CurrentPage')?.setValue(1);
        form?.updateValueAndValidity();

        this.fetchCustomForms();
    }

    onFieldSelected = (newField: string) => {
        const orderBy =  this.getPagingControl('OrderBy');
        const ascending = this.getPagingControl('Ascending');
        
        ascending?.setValue(orderBy?.value === newField ? !ascending?.value : false);
        orderBy?.setValue(newField);

        ascending?.updateValueAndValidity();
        orderBy?.updateValueAndValidity();

        this.resetCurrentPage();
 
         this.fetchCustomForms();
    }

    onPageChange = (page: number) => {
        this.setCurrentPage(page);

        this.fetchCustomForms();
    }

    private resetCurrentPage = () => {
        this.setCurrentPage(1);
    }

    private setCurrentPage = (page: number) => {
        const currentPage =  this.getPagingControl('CurrentPage');
        currentPage?.setValue(page);
        currentPage?.updateValueAndValidity();
    }

    private generatePagingModel = () : PagedModel => {
        const { Ascending, OrderBy, SearchBy, CurrentPage }: { Ascending?: boolean, OrderBy?: string, SearchBy?: string, CurrentPage?: number } = this.getPagingFormGroup()?.value;

        const offset =  ((CurrentPage ?? 1) - 1) * this.limit;

        return {
            offset,
            limit: this.limit,
            ascending: Ascending,
            orderBy: OrderBy,
            searchBy: SearchBy
        }
    }

    private fetchCustomForms = () => {
        const paging = this.generatePagingModel();
        this.reloadQueue.generateAndEnqueRequest(this.generateFetchCustomForms(paging));
    }

    private generateFetchCustomForms = (paging: PagedModel) => () => {
        const action = this.sessionService.user?.UserID === this.targetUserId ?
            this.customFormService.getPublishedCustomFormsForIndividual(paging) :
            this.customFormService.getPublishedCustomFormsForPortal(paging);

        this.isBusy = true;

        return firstValueFrom(action)
            .then((response: PagedResponse<CustomFormSelectorDTO>) => {
                this.pagedResponse = response;
                return response;
            }).finally(() => {
                this.isBusy = false;
            });
    }
}
